import { apiUrlFromScriptSrc, findEmbedScript } from './embed-script';
import type {
  PublicGrowthWidgetBootstrap,
  PublicGrowthWidgetConfig,
  PublicWebChatConfig,
  PublicWidgetBootstrap,
  PublicWidgetUnavailableReason,
} from './types';

const UNAVAILABLE_REASONS: ReadonlySet<string> = new Set([
  'WIDGET_DISABLED',
  'PLAN_INACTIVE',
  'SUBSCRIPTION_INACTIVE',
  'SUBSCRIPTION_EXPIRED',
  'ORGANIZATION_SUSPENDED',
  'CONFIGURATION_INVALID',
]);

export function isPublicWidgetAvailable(
  bootstrap: PublicWidgetBootstrap | null | undefined,
): bootstrap is PublicWidgetBootstrap & { config: PublicWebChatConfig } {
  return (
    bootstrap?.available === true &&
    bootstrap.config != null &&
    typeof bootstrap.config === 'object'
  );
}

export async function fetchPublicConfig(
  apiUrl: string,
  publicKey: string,
): Promise<PublicWidgetBootstrap> {
  const payload = await fetchBootstrapPayload(
    apiUrl,
    `/public/webchat/${encodeURIComponent(publicKey)}`,
  );
  return normalizePublicBootstrap(payload);
}

/** Growth widget config younger than this is reused without a network call. */
export const GROWTH_WIDGET_CONFIG_STALE_MS = 10 * 60_000;

type GrowthWidgetConfigCacheEntry = {
  promise: Promise<PublicGrowthWidgetBootstrap>;
  fetchedAt: number;
};

const growthWidgetConfigCache = new Map<string, GrowthWidgetConfigCacheEntry>();

/**
 * Loads the growth widget config once per widget and shares in-flight
 * requests, so concurrent callers and remounts never issue duplicate fetches.
 */
export function loadGrowthWidgetConfig(
  apiUrl: string,
  publicId: string,
  staleMs: number = GROWTH_WIDGET_CONFIG_STALE_MS,
): Promise<PublicGrowthWidgetBootstrap> {
  const key = `${apiUrl}|${publicId}`;
  const cached = growthWidgetConfigCache.get(key);
  if (cached && Date.now() - cached.fetchedAt < staleMs) {
    return cached.promise;
  }

  const entry: GrowthWidgetConfigCacheEntry = {
    promise: fetchGrowthWidgetConfig(apiUrl, publicId),
    fetchedAt: Date.now(),
  };
  growthWidgetConfigCache.set(key, entry);
  entry.promise.catch(() => {
    if (growthWidgetConfigCache.get(key) === entry) {
      growthWidgetConfigCache.delete(key);
    }
  });
  return entry.promise;
}

/** Clears cached growth widget configs. Intended for tests. */
export function resetGrowthWidgetConfigCache(): void {
  growthWidgetConfigCache.clear();
}

export async function fetchGrowthWidgetConfig(
  apiUrl: string,
  publicId: string,
): Promise<PublicGrowthWidgetBootstrap> {
  const payload = await fetchBootstrapPayload(
    apiUrl,
    `/public/widgets/${encodeURIComponent(publicId)}`,
    'default',
  );
  if (!payload || typeof payload !== 'object') {
    return { available: false, reason: 'CONFIGURATION_INVALID' };
  }
  const record = payload as Record<string, unknown>;
  if (record.available !== true || !isGrowthConfig(record.config)) {
    return {
      available: false,
      reason: parseUnavailableReason(record.reason) ?? 'CONFIGURATION_INVALID',
    };
  }
  return {
    available: true,
    reason: null,
    config: record.config,
  };
}

async function fetchBootstrapPayload(
  apiUrl: string,
  path: string,
  cache: RequestCache = 'no-store',
): Promise<unknown> {
  const endpoint = joinApiUrl(apiUrl, path);
  let response = await fetch(endpoint, {
    method: 'GET',
    // 'default' lets the browser honor Cache-Control/ETag from the API; a raw
    // 304 without a JSON body is still handled by the reload below.
    cache,
    headers: { Accept: 'application/json' },
  });

  if (response.status === 304) {
    const retryUrl = new URL(endpoint);
    retryUrl.searchParams.set('_', Date.now().toString());
    response = await fetch(retryUrl.toString(), {
      method: 'GET',
      cache: 'reload',
      headers: { Accept: 'application/json' },
    });
  }

  if (
    response.status === 401 ||
    response.status === 403 ||
    response.status === 404
  ) {
    return {
      available: false,
      reason: 'CONFIGURATION_INVALID',
    };
  }

  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(
      detail || `Failed to load widget config (${response.status}).`,
    );
  }

  return response.json();
}

export function normalizePublicBootstrap(
  payload: unknown,
): PublicWidgetBootstrap {
  if (!payload || typeof payload !== 'object') {
    return { available: false, reason: 'CONFIGURATION_INVALID' };
  }

  const record = payload as Record<string, unknown>;
  if (typeof record.available === 'boolean') {
    const reason = parseUnavailableReason(record.reason);
    if (record.available !== true) {
      return { available: false, reason: reason ?? 'CONFIGURATION_INVALID' };
    }
    const config = isPublicConfig(record.config) ? record.config : null;
    if (!config) {
      return { available: false, reason: 'CONFIGURATION_INVALID' };
    }
    return { available: true, reason: null, config };
  }

  // Legacy unwrapped config payload.
  if (isPublicConfig(payload)) {
    return { available: true, reason: null, config: payload };
  }

  return { available: false, reason: 'CONFIGURATION_INVALID' };
}

function parseUnavailableReason(
  value: unknown,
): PublicWidgetUnavailableReason | null {
  return typeof value === 'string' && UNAVAILABLE_REASONS.has(value)
    ? (value as PublicWidgetUnavailableReason)
    : null;
}

function isPublicConfig(value: unknown): value is PublicWebChatConfig {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.publicKey === 'string' && typeof record.name === 'string'
  );
}

function isGrowthConfig(value: unknown): value is PublicGrowthWidgetConfig {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.publicId === 'string' &&
    typeof record.name === 'string' &&
    Array.isArray(record.channels)
  );
}

/** Builds an API endpoint from the configured public API base URL. */
export function joinApiUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

function normalizeApiUrl(value?: string): string | undefined {
  const candidates = (value ?? '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
  const candidate = candidates[candidates.length - 1];
  if (!candidate) return undefined;

  try {
    const url = new URL(candidate);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return undefined;
    }
    return candidate.replace(/\/+$/, '');
  } catch {
    return undefined;
  }
}

/**
 * Resolves the API base URL for the initial public config fetch.
 * Defaults to the origin that serves widget.js.
 */
export function resolveBootstrapApiUrl(
  script: HTMLScriptElement | null = findEmbedScript(),
): string {
  const fromWindow = normalizeApiUrl(window.Revocom?.apiUrl);
  if (fromWindow) {
    return fromWindow;
  }

  const fromScript = apiUrlFromScriptSrc(script);
  if (fromScript) {
    return fromScript;
  }

  throw new Error(
    '[Revocom Widget] Unable to resolve API URL from the widget script src.',
  );
}

/**
 * Resolves the API base URL for session init and realtime transport.
 * Prefers the server-advertised apiBaseUrl when the widget and API hosts differ.
 */
export function resolveSessionApiUrl(
  configApiUrl?: string,
  script: HTMLScriptElement | null = findEmbedScript(),
): string {
  const fromConfig = normalizeApiUrl(configApiUrl);
  if (fromConfig) {
    return fromConfig;
  }

  return resolveBootstrapApiUrl(script);
}

/** @deprecated Use {@link resolveBootstrapApiUrl} or {@link resolveSessionApiUrl}. */
export function resolveApiUrl(configApiUrl?: string): string {
  if (configApiUrl) {
    return resolveSessionApiUrl(configApiUrl);
  }
  return resolveBootstrapApiUrl();
}

export function websocketOriginFromEndpoint(endpoint: string): string {
  try {
    const url = new URL(endpoint);
    return url.origin;
  } catch {
    return endpoint.replace(/\/webchat\/?$/, '').replace(/\/+$/, '');
  }
}
